import { Link, useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Wallet, TrendingUp, UserPlus, Users, Copy, Share2, Zap, Clock } from "lucide-react";
import { toast } from "sonner";
import { StatCard, Panel, PageHead, Label } from "@/components/common";
import { inr } from "@/lib/api";
import { Leaderboard } from "@/components/Leaderboard";
import { TodayIncome } from "@/components/TodayIncome";

const Banner = ({ me }) => {
  const st = me.user.status;
  if (st === "active") return null;
  if (st === "deactivated") return (
    <div data-testid="status-banner" className="mb-8 rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
      <p className="font-display text-xl font-bold text-white">Your account is deactivated</p>
      <p className="text-sm text-slate-300">You can't earn bonuses, add money or withdraw right now. Please contact the admin.</p>
    </div>
  );
  const pending = st === "pending";
  return (
    <div data-testid="status-banner" className={`mb-8 flex flex-col gap-4 rounded-2xl border p-6 sm:flex-row sm:items-center sm:justify-between ${pending ? "border-amber-500/30 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/10"}`}>
      <div className="flex items-start gap-4">
        {pending ? <Clock className="mt-1 h-5 w-5 text-amber-400" /> : <Zap className="mt-1 h-5 w-5 text-emerald-400" />}
        <div>
          <p className="font-display text-xl font-bold text-white">{pending ? "Payment under verification" : "Activate your ID to start earning"}</p>
          <p className="text-sm text-slate-300">{pending ? `UTR ${me.pending_payment?.utr} submitted. The admin will verify it shortly.` : `Buy any ${inr(me.settings.package_price)} product to activate your ID instantly after payment is confirmed.`}</p>
        </div>
      </div>
      {!pending && <Link to="/dashboard/shop" data-testid="banner-activate-btn" className="rounded-full bg-emerald-500 px-6 py-2.5 text-center text-sm font-semibold text-[#0B0F17] hover:bg-emerald-400">Buy & activate</Link>}
    </div>
  );
};

const ReferralHub = ({ me }) => {
  const link = `${window.location.origin}/sign-up?ref=${me.user.referral_code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied"); };
  const wa = `https://wa.me/?text=${encodeURIComponent(`Join my Indian Small Capital team and earn ₹${me.settings.referral_bonus} per referral: ${link}`)}`;
  return (
    <Panel className="lg:col-span-2">
      <Label>Referral hub</Label>
      <h3 className="mt-2 font-display text-2xl font-bold text-white">Share your link</h3>
      <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-center">
        <div className="rounded-xl bg-white p-3" data-testid="referral-qr"><QRCodeSVG value={link} size={112} /></div>
        <div className="min-w-0 flex-1 space-y-3">
          <p className="text-xs text-slate-500">Your code <span className="font-mono text-base text-emerald-400" data-testid="referral-code">{me.user.referral_code}</span></p>
          <div className="truncate rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-2.5 font-mono text-xs text-slate-300" data-testid="referral-link">{link}</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copy} data-testid="copy-referral-link-button" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-[#0B0F17] hover:bg-emerald-400"><Copy className="h-4 w-4" />Copy link</button>
            <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-share-button" className="inline-flex items-center gap-2 rounded-full border border-[#1F293D] px-4 py-2 text-sm hover:border-emerald-500/50"><Share2 className="h-4 w-4" />WhatsApp</a>
          </div>
        </div>
      </div>
      {me.user.status !== "active" && <p className="mt-4 text-xs text-amber-400">You earn bonuses once your own ID is active too — activate soon.</p>}
    </Panel>
  );
};

const SponsorBox = ({ me }) => (
  <Panel>
    <Label>Your sponsor</Label>
    {me.sponsor ? (
      <div className="mt-4" data-testid="sponsor-info">
        <p className="font-display text-2xl font-bold text-white">{me.sponsor.name}</p>
        <p className="font-mono text-sm text-emerald-400">{me.sponsor.referral_code}</p>
      </div>
    ) : <p className="mt-4 text-sm text-slate-400" data-testid="sponsor-info">No sponsor linked. {me.user.status !== "active" && <Link className="text-emerald-400 underline" to="/dashboard/activate">Add one</Link>}</p>}
    <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#1F293D] pt-4 text-sm">
      <div><p className="text-slate-500">Active directs</p><p className="font-mono text-white" data-testid="active-directs">{me.stats.direct_active}</p></div>
      <div><p className="text-slate-500">Active team</p><p className="font-mono text-white">{me.stats.team_active}</p></div>
    </div>
  </Panel>
);

export default function Overview() {
  const { me } = useOutletContext();
  return (
    <div data-testid="member-overview">
      <PageHead label="Dashboard" title={`Welcome, ${me.user.name.split(" ")[0]}`} />
      <TodayIncome />
      <Banner me={me} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        <StatCard label="Wallet balance" value={inr(me.user.wallet)} icon={Wallet} testId="stat-wallet" />
        <StatCard label="Total earned" value={inr(me.user.total_earned)} icon={TrendingUp} accent="text-amber-400" testId="stat-earned" delay={0.05} />
        <StatCard label="Direct referrals" value={me.stats.direct} icon={UserPlus} accent="text-indigo-400" testId="stat-direct" delay={0.1} />
        <StatCard label="Team size" value={me.stats.team_size} icon={Users} accent="text-sky-400" testId="stat-team" delay={0.15} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <ReferralHub me={me} />
        <SponsorBox me={me} />
      </div>
      <Leaderboard />
    </div>
  );
}
